const ARABIC_DIACRITICS_RE = /[\u064B-\u065F\u0670\u06D6-\u06ED]/g;
const TATWEEL_RE = /\u0640/g;
const NON_SEARCH_CHARS_RE = /[^0-9a-zA-Z\u0600-\u06FF\s]/g;
const MULTI_SPACE_RE = /\s+/g;

const DEFAULT_SEARCH_FIELDS = [
  "title",
  "title_en",
  "title_ar",
  "name",
  "productName",
  "serviceName",
  "author",
  "brand",
  "description",
  "description_en",
  "description_ar",
  "category",
  "subcategory",
  "subCategory",
  "sub_category",
  "subcategory_id",
  "category_id",
  "type",
  "condition",
  "tags",
  "keywords",
  "sellerName",
  "seller_name",
  "seller.name",
  "contactName",
  "location",
  "locationText",
  "state",
  "city",
  "country",
  "university",
  "academicLevel",
  "contentType",
  "instructor",
  "organization",
  "org_name",
];

function valueToStrings(value) {
  if (value == null) return [];
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return [String(value)];
  }
  if (Array.isArray(value)) {
    return value.flatMap(valueToStrings);
  }
  if (typeof value === "object") {
    return Object.values(value).flatMap(valueToStrings);
  }
  return [];
}

function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export function normalizeForSearch(input = "") {
  return String(input)
    .trim()
    .toLowerCase()
    .replace(ARABIC_DIACRITICS_RE, "")
    .replace(TATWEEL_RE, "")
    .replace(/[أإآٱ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .replace(/ة/g, "ه")
    .replace(NON_SEARCH_CHARS_RE, " ")
    .replace(MULTI_SPACE_RE, " ")
    .trim();
}

function splitTokens(text = "") {
  const normalized = normalizeForSearch(text);
  if (!normalized) return [];
  return normalized.split(" ").filter(Boolean);
}

function expandArabicToken(token) {
  const results = new Set();
  const queue = [normalizeForSearch(token)];

  while (queue.length) {
    const current = queue.shift();
    if (!current || current.length < 2 || results.has(current)) continue;

    results.add(current);

    // للشحن -> الشحن + شحن
    if (current.startsWith("لل") && current.length > 3) {
      queue.push("ال" + current.slice(2));
      queue.push(current.slice(2));
    }

    // الشحن -> شحن
    if (current.startsWith("ال") && current.length > 3) {
      queue.push(current.slice(2));
    }

    // Common one-letter prefixes: و / ف / ب / ك / ل
    const oneLetterPrefixes = ["و", "ف", "ب", "ك", "ل"];
    for (const prefix of oneLetterPrefixes) {
      if (current.startsWith(prefix) && current.length > 3) {
        queue.push(current.slice(1));
      }
    }
  }

  return [...results].filter((item) => item.length >= 2);
}

function buildTokenSetFromText(text) {
  const tokenSet = new Set();
  const tokens = splitTokens(text);
  for (const token of tokens) {
    for (const variant of expandArabicToken(token)) {
      tokenSet.add(variant);
    }
  }
  return tokenSet;
}

export function buildSearchIndex(item, fields = DEFAULT_SEARCH_FIELDS) {
  const collected = [];
  for (const field of fields) {
    const value = getByPath(item, field);
    collected.push(...valueToStrings(value));
  }
  const rawText = collected.join(" ");
  const normalizedText = normalizeForSearch(rawText);
  const tokenSet = buildTokenSetFromText(rawText);
  return { rawText, normalizedText, tokenSet };
}

function ensureIndex(itemOrIndex, fields = DEFAULT_SEARCH_FIELDS) {
  if (
    itemOrIndex &&
    typeof itemOrIndex === "object" &&
    "normalizedText" in itemOrIndex &&
    "tokenSet" in itemOrIndex
  ) {
    return itemOrIndex;
  }
  if (itemOrIndex && itemOrIndex.__searchIndex) {
    return itemOrIndex.__searchIndex;
  }
  return buildSearchIndex(itemOrIndex, fields);
}

export function matchesUnifiedSearch(itemOrIndex, query, fields = DEFAULT_SEARCH_FIELDS) {
  const normalizedQuery = normalizeForSearch(query);
  if (!normalizedQuery) return true;

  const index = ensureIndex(itemOrIndex, fields);
  const queryTokens = splitTokens(normalizedQuery);

  return queryTokens.every((queryToken) => {
    const variants = expandArabicToken(queryToken);
    return variants.some((variant) => {
      if (!variant) return false;
      if (index.tokenSet.has(variant)) return true;
      if (index.normalizedText.includes(variant)) return true;
      return false;
    });
  });
}

export function attachSearchIndex(items = [], fields = DEFAULT_SEARCH_FIELDS) {
  return items.map((item) => ({
    ...item,
    __searchIndex: buildSearchIndex(item, fields),
  }));
}

export function filterWithUnifiedSearch(items = [], query, fields = DEFAULT_SEARCH_FIELDS) {
  if (!query || !String(query).trim()) return items;
  return items.filter((item) => matchesUnifiedSearch(item, query, fields));
}

export const SEARCH_FIELDS_COMMON = DEFAULT_SEARCH_FIELDS;
